'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useFocProfileQuery } from '@/hooks/react-query/community-v2/use-foc-profile.query'
import { useSaveFocProfileMutation } from '@/hooks/react-query/community-v2/use-save-foc-profile.mutation'
import { useUserQuery } from '@/hooks/react-query/user/use-user'
import { Link } from '@/i18n/navigation'
import { KolProfile } from '@/types/campaign'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { CampaignAddressFields } from './campaign-address-fields'
import {
  CampaignRegisterFormData,
  campaignRegisterDefaultValues,
  createCampaignRegisterSchema,
} from './campaign-register-schema'
import { CampaignSocialLinksField } from './campaign-social-links-field'

interface CampaignKolProfileModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: (profile: KolProfile) => void
}

export const CampaignKolProfileModal = ({
  open,
  onOpenChange,
  onComplete,
}: CampaignKolProfileModalProps) => {
  const t = useTranslations('community_threads.campaign')
  const tFields = useTranslations('community_threads.campaign.fields')
  const { data: user } = useUserQuery()
  const { data: focProfile } = useFocProfileQuery()
  const { mutateAsync: saveKolProfile, isPending } = useSaveFocProfileMutation()

  const form = useForm<CampaignRegisterFormData>({
    resolver: zodResolver(
      createCampaignRegisterSchema(t, { requireBodyInfo: true })
    ),
    defaultValues: campaignRegisterDefaultValues,
  })
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = form

  // Tỉnh/quận/phường có sẵn từ user, đưa qua CampaignAddressFields để đi
  // đúng luồng match/normalize với address.json, tránh set thẳng giá trị
  // thô không khớp options khiến 3 ô không hiển thị được.
  const initialAddress = useMemo(() => {
    const profile = focProfile
    if (profile?.city || profile?.district || profile?.ward) {
      return {
        city: profile.city,
        district: profile.district,
        ward: profile.ward,
      }
    }
    const shipping = user?.shipping_address
    if (!shipping?.city && !shipping?.district && !shipping?.ward) {
      return undefined
    }
    return {
      city: shipping.city ?? '',
      district: shipping.district ?? '',
      ward: shipping.ward ?? '',
    }
  }, [focProfile, user])

  const GENDER_OPTIONS = [
    { label: tFields('gender_option_male'), value: 'MALE' },
    { label: tFields('gender_option_female'), value: 'FEMALE' },
  ]

  useEffect(() => {
    if (!open) return
    const profile = focProfile
    // Ưu tiên dữ liệu hồ sơ KOL đã lưu (API /foc-profiles/me), thiếu gì thì
    // rơi về thông tin tài khoản chung để đỡ phải gõ lại từ đầu.
    reset({
      ...campaignRegisterDefaultValues,
      // Không bắt buộc chọn - chưa có thì để trống, Select tự hiện placeholder.
      gender_type: profile?.gender_type || '',
      fullName: profile?.fullName || user?.fullname || '',
      phone: profile?.phone || user?.phone || '',
      email: profile?.email || user?.email || '',
      socialLinks: profile?.socialLinks ? [...profile.socialLinks] : [],
      sports: profile?.sports ?? '',
      height: profile?.height ?? '',
      weight: profile?.weight ?? '',
      address:
        profile?.address ||
        user?.shipping_address?.address ||
        user?.address ||
        '',
    })
  }, [open, reset, user, focProfile])

  const onSubmit = async (data: CampaignRegisterFormData) => {
    const profile: KolProfile = {
      gender_type: data.gender_type,
      fullName: data.fullName,
      phone: data.phone,
      email: data.email,
      socialLinks: data.socialLinks,
      sports: data.sports,
      height: data?.height,
      weight: data?.weight,
      address: data.address,
      city: data.city,
      district: data.district,
      ward: data.ward,
    }

    try {
      const response = await saveKolProfile(profile)
      onComplete(response.data)
      onOpenChange(false)
    } catch {
      toast.error(t('profile_modal.save_error'))
    }
  }

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent
        className='gap-0 border-none px-5 py-6 lg:max-h-[85vh] lg:max-w-xl lg:p-8'
        aria-describedby='modal-campaign-kol-profile'
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />

        <div className='no-scrollbar max-h-[80vh] overflow-y-auto px-1 pb-2 lg:max-h-[calc(85vh-4rem)]'>
          <h3 className='mb-1 font-criteria text-2xl font-bold text-neutral-900'>
            {t('profile_modal.title')}
          </h3>
          <p className='mb-5 text-sm text-neutral-600'>
            {t('profile_modal.description')}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <Input
              id='email'
              type='email'
              label={tFields('email_label')}
              placeholder={tFields('email_placeholder')}
              {...register('email')}
              error={errors.email}
              wrapperClassName='mt-1'
            />

            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <Input
                id='fullName'
                label={tFields('full_name_label')}
                placeholder={tFields('full_name_placeholder')}
                {...register('fullName')}
                error={errors.fullName}
                wrapperClassName='mt-1'
              />
              <div className='mt-1 space-y-1'>
                <span className='ml-3 block font-sans text-sm leading-4 text-neutral-600'>
                  {tFields('gender_label')}
                </span>
                <Select
                  placeholder={tFields('gender_placeholder')}
                  value={watch('gender_type') || undefined}
                  onValueChange={(value) =>
                    setValue('gender_type', value as 'MALE' | 'FEMALE', {
                      shouldValidate: true,
                    })
                  }
                  options={GENDER_OPTIONS}
                  error={errors.gender_type}
                />
              </div>
            </div>

            <Input
              id='phone'
              type='tel'
              label={tFields('phone_label')}
              placeholder={tFields('phone_placeholder')}
              {...register('phone')}
              error={errors.phone}
              wrapperClassName='mt-1'
            />

            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <Input
                id='height'
                label={tFields('height_label')}
                placeholder={tFields('height_placeholder')}
                {...register('height')}
                error={errors.height}
                wrapperClassName='mt-1'
              />
              <Input
                id='weight'
                label={tFields('weight_label')}
                placeholder={tFields('weight_placeholder')}
                {...register('weight')}
                error={errors.weight}
                wrapperClassName='mt-1'
              />
            </div>

            <Input
              id='sports'
              label={tFields('sports_label')}
              placeholder={tFields('sports_placeholder')}
              {...register('sports')}
              error={errors.sports}
              wrapperClassName='mt-1'
            />

            <CampaignAddressFields
              form={form}
              initialAddress={initialAddress}
              addressLabel={tFields('address_label')}
            />

            <CampaignSocialLinksField
              label={tFields('social_links_label')}
              placeholder={tFields('social_links_placeholder')}
              value={watch('socialLinks')}
              onChange={(value) =>
                setValue('socialLinks', value, { shouldValidate: true })
              }
              error={errors.socialLinks}
            />

            <Checkbox
              title={
                <>
                  {t('profile_modal.policy_agree_prefix')}{' '}
                  <Link
                    href='/page/chinh-sach-bao-mat-thong-tin-ca-nhan'
                    className='text-primary underline hover:opacity-90'
                    target='_blank'
                  >
                    {t('profile_modal.policy_agree_link')}
                  </Link>
                </>
              }
              checked={watch('agreePolicy')}
              onCheckedChange={(value) =>
                setValue('agreePolicy', value === true, {
                  shouldValidate: true,
                })
              }
              titleClassName='text-sm'
            />
            {errors.agreePolicy && (
              <p className='text-sm text-error-500'>
                {errors.agreePolicy.message}
              </p>
            )}

            <div className='flex gap-3'>
              <Button
                type='button'
                variant='outline-neutral'
                className='flex-1'
                onClick={() => onOpenChange(false)}
              >
                {t('profile_modal.cancel')}
              </Button>
              <Button
                type='submit'
                variant='black'
                className='flex-1'
                loading={isPending}
              >
                {t('profile_modal.submit')}
                <ArrowRight size={16} />
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
